import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {DetectMethodChanges, isOptionExpired, isVoid, jsonCloneObject} from "../../../utils";
import {HgOriginalClientPositionsService} from "../portfolio-selector/hg-original-client-positions.service";
import {ApgPortfolio} from "../../../adjustment-pricing-grid/model/ApgPortfolio";
import {HedgePosition} from "../../data-model/hedge-position";
import * as Enumerable from "linq";
import {MessageBusService} from "../../../message-bus.service";
import {HgPortfolioSelectorComponent} from "../portfolio-selector/hg-portfolio-selector.component";
import {OptionsChainService} from "../../../option-chains.service";

interface HedgingLeg {
    qty: number;
    side: 'Buy' | 'Sell' | 'Flat';
    type: 'Call' | 'Put';
    expiration: string;
    strike: number;
    position: HedgePosition;
}

interface HedgingGroup {
    label: string;
    color: string;
    groupId: string;
    legs: HedgingLeg[];
}

interface AfterStatePreviewRowTransaction {
    qty?: number;
    operation?: 'Buy' | 'Sell';
    type: 'Call' | 'Put'
    expiration?: string;
    strike?: number;
}

interface AfterStatePreviewRow {
    label?: string;
    color?: string;
    before: HedgePosition[];
    after: HedgePosition[];
    transaction: AfterStatePreviewRowTransaction[];
}



@Component({
    selector: 'ets-hg-hedge-positions-sync-dialog',
    templateUrl: 'hg-hedge-positions-sync-dialog.component.html',
    styleUrls: ['./hg-hedge-positions-sync-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class HgHedgePositionsSyncDialogComponent implements OnInit {
    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _originalStateService: HgOriginalClientPositionsService,
        private readonly _messageBus: MessageBusService,
        private readonly _optionChainService: OptionsChainService
    ) {
    }

    private _formerCallPackages: HedgingGroup[] = [];
    private _formerPutPackages: HedgingGroup[] = [];
    private _currentCallPackages: HedgingGroup[] = [];
    private _currentPutPackages: HedgingGroup[] = [];
    private _portfolio: ApgPortfolio;

    visible = false;

    get hasCurrentState(): boolean {
        return !isVoid(this._currentCallPackages) ||
            !isVoid(this._currentPutPackages);
    }

    get hasPutRows() : boolean {
        return !isVoid(this.putRows);
    }

    ngOnInit() {
    }

    get formerCallPackages() {
        return this._formerCallPackages;
    }

    get formerPutPackages() {
        return this._formerPutPackages;
    }

    get currentCallPackages() {
        return this._currentCallPackages;
    }

    get currentPutPackages() {
        return this._currentPutPackages;
    }

    callRows: AfterStatePreviewRow[] = [];
    putRows: AfterStatePreviewRow[] = [];

    @DetectMethodChanges({isAsync: true})
    async show(portfolio: ApgPortfolio, comp: HgPortfolioSelectorComponent) {

        const hasOwnChanges = await comp.hasOwnChanges();
        const hasTheirChanges = comp.hasTheirChanges();

        if (!hasOwnChanges && !hasTheirChanges) {
            return;
        }

        const originalPositions = this._originalStateService
            .getTheirOriginalPositions(portfolio);

        const currentPositions = hasTheirChanges ? this._originalStateService
            .getTheirCurrentPositions(portfolio) : [];

        if (isVoid(originalPositions) && isVoid(currentPositions)) {
            return;
        }

        this._portfolio = portfolio;

        this.callRows = await this.setupRows(originalPositions, currentPositions, 'Call');
        this.putRows = await this.setupRows(originalPositions, currentPositions, 'Put');

        const originalPosition = originalPositions.find(x => !isVoid(x.ticker));
        const currentPosition = currentPositions.find(x => !isVoid(x.ticker));

        let isOriginalExpired = false;
        let isCurrentExpired = false;

        if (!isVoid(originalPosition)) {
            isOriginalExpired = this._optionChainService.isOptionExpired(originalPosition.ticker);
        } else {
            isOriginalExpired = true;
        }

        if (hasTheirChanges) {
            if (!isVoid(currentPosition)) {
                isCurrentExpired = this._optionChainService.isOptionExpired(currentPosition.ticker);
            }
        }

        if (isOriginalExpired && isCurrentExpired) {
            return;
        }

        if (!isOriginalExpired) {
            this._formerCallPackages = this.makeHedgingGroups(originalPositions.filter(x => x.type === 'Call'));
            this._formerPutPackages = this.makeHedgingGroups(originalPositions.filter(x => x.type === 'Put'));
        }

        if (!isCurrentExpired) {
            if (!isVoid(currentPositions)) {
                this._currentCallPackages = this.makeHedgingGroups(currentPositions.filter(x => x.type === 'Call'));
                this._currentPutPackages = this.makeHedgingGroups(currentPositions.filter(x => x.type === 'Put'));
            }
        }

        this.visible = true;

        setTimeout(() => this._changeDetector.detectChanges(), 100);
    }

    @DetectMethodChanges()
    onHidden() {
        this.visible = false;

        this._formerPutPackages = null;
        this._formerPutPackages = null;
        this._currentCallPackages = null;
        this._currentPutPackages = null;
        this._portfolio = null;

    }

    getWidth() {
        let width = 1060;
        return width;
    }

    applyState(header: 'former' | 'current') {
        this._messageBus.publish({
            topic: 'Hg.RefreshClientPositions',
            payload: {
                state: header,
                portfolio: this._portfolio
            }
        });
        this.onHidden();
    }

    private makeHedgingGroups(hedgePositions: HedgePosition[]): HedgingGroup[] {
        const groups = Enumerable.from(hedgePositions)
            .orderByDescending(x => x.strike)
            .groupBy(x => `${x.label}^${x.color}^${x.groupId}`)
            .select(grp => {

                const key = grp.key();
                const parts = key.split('^');

                const label = parts[0];
                const color = parts[1];
                const groupId = parts[2];

                const legs: HedgingLeg[] = grp.select(item => {
                    const leg: HedgingLeg = {
                        position: item,
                        side: item.qty === 0 ? 'Flat' : (item.qty > 0 ? 'Buy' : 'Sell'),
                        qty: item.qty,
                        type: item.type,
                        expiration: item.expiration,
                        strike: item.strike
                    };
                    return leg;
                }).toArray();

                const res: HedgingGroup = {
                    label,
                    color,
                    groupId,
                    legs
                };

                return res;
            }).toArray();

        return groups;
    }

    private async setupRows(formerPositions: HedgePosition[], currentPositions: HedgePosition[], side: 'Call' | 'Put')
    : Promise<AfterStatePreviewRow[]> {

        const afterStateById = Enumerable.from(currentPositions)
            .where(x => x.type === side)
            .groupBy(x => x.groupId)
            .select(x => {
                const items = x.toArray();
                const label = items[0].label;
                const color = items[0].color;

                return {
                    id: x.key(),
                    label,
                    color,
                    items
                }
            }).toArray();

        const currentStateById = Enumerable.from(formerPositions)
            .where(x => x.type === side)
            .groupBy(x => x.groupId)
            .select(x => {
                const items = x.toArray();
                const label = items[0].label;
                const color = items[0].color;

                return {
                    id: x.key(),
                    label,
                    color,
                    items
                }
            }).toArray();

        const newHedges = afterStateById.filter(grp =>
            currentStateById.every(x => x.id !== grp.id));

        const removedHedges = currentStateById.filter(grp =>
            afterStateById.every(x => x.id !== grp.id));

        const changedHedges = currentStateById.filter(grp =>
            afterStateById.some(x => x.id === grp.id));

        const newHedgeRows = newHedges.map(newHedge => {
            const before = [];

            const transaction: AfterStatePreviewRowTransaction[] = newHedge.items.map(item => {
                const t: AfterStatePreviewRowTransaction = {
                    strike: item.strike,
                    expiration: item.expiration,
                    operation: item.qty > 0 ? 'Buy' : 'Sell',
                    qty: item.qty,
                    type: item.type,
                };
                return t;
            });

            const after = jsonCloneObject(newHedge.items);

            const row: AfterStatePreviewRow = {
                label: newHedge.label,
                color: newHedge.color,
                before,
                after,
                transaction,
            };

            return row;
        });

        console.log('new hedges rows', newHedgeRows);

        const removedHedgeRows = removedHedges.map(removedHedge => {
            const before = jsonCloneObject(removedHedge.items);

            const transaction: AfterStatePreviewRowTransaction[] = removedHedge.items.map(item => {
                const t: AfterStatePreviewRowTransaction = {
                    strike: item.strike,
                    expiration: item.expiration,
                    operation: item.qty > 0 ? 'Sell' : 'Buy',
                    qty: item.qty * -1,
                    type: item.type,
                };
                return t;
            });

            const after = [];

            const row: AfterStatePreviewRow = {
                label: removedHedge.label,
                color: removedHedge.color,
                before,
                after,
                transaction,
            };

            return row;
        });

        console.log('removed hedges rows', newHedgeRows);

        const changedHedgesRows = changedHedges.map(changedHedge => {

            const originalHedge = currentStateById
                .find(grp => grp.id === changedHedge.id)?.items || [];

            const destinationHedge = afterStateById
                .find(grp => grp.id === changedHedge.id)?.items || [];

            const newLegs = destinationHedge.filter(x =>
                originalHedge.findIndex(xx => xx.ticker === x.ticker) === -1)
                .map(x => x.ticker);

            const removedLegs = originalHedge.filter(ol =>
                destinationHedge.findIndex(dl => dl.ticker === ol.ticker) === -1)
                .map(x => x.ticker);

            const changedLegs = destinationHedge.filter(dleg =>
                originalHedge.findIndex(oleg => oleg.ticker === dleg.ticker && oleg.qty !== dleg.qty) >= 0)
                .map(x => x.ticker);

            const newTrans: AfterStatePreviewRowTransaction[] = newLegs.map(newLeg => {
                const leg = destinationHedge.find(x => x.ticker === newLeg);
                return {
                    operation: leg.qty > 0 ? 'Buy' : 'Sell',
                    expiration: leg.expiration,
                    qty: leg.qty,
                    strike: leg.strike,
                    type: leg.type,
                };
            });

            const removeTrans: AfterStatePreviewRowTransaction[] = removedLegs.map(removedLeg => {
                const leg = originalHedge.find(x => x.ticker === removedLeg);
                return {
                    operation: leg.qty > 0 ? 'Buy' : 'Sell',
                    expiration: leg.expiration,
                    qty: leg.qty,
                    strike: leg.strike,
                    type: leg.type
                };
            });

            const changeTrans = changedLegs.map(changedLeg => {
                const originalLeg = originalHedge.find(x => x.ticker === changedLeg);
                const destinationLeg = destinationHedge.find(x => x.ticker === changedLeg);

                console.assert(destinationLeg.strike === originalLeg.strike);
                console.assert(destinationLeg.expiration === originalLeg.expiration);

                const qty = destinationLeg.qty - originalLeg.qty;

                const tr: AfterStatePreviewRowTransaction = {
                    strike: destinationLeg.strike,
                    expiration: destinationLeg.expiration,
                    qty: qty,
                    operation: qty > 0 ? 'Buy' : 'Sell',
                    type: destinationLeg.type,
                }

                return tr;
            });

            const before = originalHedge;
            const after = destinationHedge;
            const transaction = newTrans.concat(removeTrans).concat(changeTrans);

            const row: AfterStatePreviewRow = {
                label: destinationHedge[0].label,
                color: destinationHedge[0].color,
                before,
                after,
                transaction
            };

            return row;
        });

        console.log('changed hedges rows', changedHedgesRows)

        const totalRows = newHedgeRows.concat(removedHedgeRows).concat(changedHedgesRows);

        const previewRows = Enumerable.from(totalRows).orderByDescending(x => {
            const positions = isVoid(x.after) ? x.before : x.after;
            const order = positions[0]?.strike;
            return order;
        }).toArray();

        return previewRows;
    }
}